import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getNestedValue, getColorClasses, resolveConditionalColor, safeDisplayValue } from './utils';

/**
 * UpdatePopover — inline edit popover for update actions
 */
const UpdatePopover = ({ isOpen, anchorRef, label, currentValue, unit, inputType, loading, onConfirm, onClose }) => {
  const [editValue, setEditValue] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePosition = () => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popoverWidth = 220;
    const margin = 8;
    const safeLeft = Math.max(margin, Math.min(rect.right - popoverWidth, window.innerWidth - popoverWidth - margin));
    setPosition({
      top: rect.bottom + 4,
      left: safeLeft,
    });
  };

  useEffect(() => {
    if (isOpen) {
      setEditValue(currentValue ?? '');
      updatePosition();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, currentValue]);

  useEffect(() => {
    if (!isOpen) return;
    const handleWindowUpdate = () => updatePosition();
    window.addEventListener('resize', handleWindowUpdate);
    window.addEventListener('scroll', handleWindowUpdate, true);
    return () => {
      window.removeEventListener('resize', handleWindowUpdate);
      window.removeEventListener('scroll', handleWindowUpdate, true);
    };
  }, [isOpen, anchorRef]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalValue = inputType === 'number' ? Number(editValue) : editValue;
    onConfirm(finalValue);
  };

  return createPortal(
    <div
      ref={popoverRef}
      data-machine-detail-safe-click="true"
      className="fixed z-[9999]"
      style={{ top: position.top, left: position.left, minWidth: '220px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-slate-800 rounded-xl p-3 shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
        style={{ border: '1px solid rgba(255,255,255,0.12)' }}
      >
        <div className="text-[11px] text-slate-400 font-bold uppercase tracking-wide mb-2">
          {label} 수정
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type={inputType || 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none transition-colors"
              placeholder={String(currentValue ?? '')}
              step={inputType === 'number' ? 'any' : undefined}
            />
            {unit && <span className="text-xs text-slate-500 font-bold shrink-0">{unit}</span>}
          </div>
          <div className="flex gap-2 mt-2.5 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3 py-1 rounded-lg text-[11px] font-bold text-slate-400 bg-slate-700/50 hover:bg-slate-700 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-1 rounded-lg text-[11px] font-bold text-white bg-indigo-500 hover:bg-indigo-400 transition-colors disabled:opacity-50"
            >
              {loading ? '저장 중...' : '확인'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export { UpdatePopover };

/**
 * MetricCard component - renders a single metric
 */
const MetricCard = ({ label, value, unit, color, showDot, hasUpdateAction, onClick }) => {
  const colors = getColorClasses(color);

  return (
    <div
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-3 rounded-2xl ${colors.bgLight} hover:brightness-125 transition-all cursor-pointer group relative`}
      style={{ border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        {showDot && <div className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />}
        <span className="text-[12px] text-slate-500 block font-bold">{label}</span>
        {hasUpdateAction && (
          <svg className="w-2.5 h-2.5 text-slate-600 group-hover:text-indigo-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        )}
      </div>
      <div className="flex items-baseline gap-0.5">
        <span className={`text-2xl font-black ${colors.text}`}>{value}</span>
        <span className="text-xs text-slate-500 font-bold">{unit}</span>
      </div>
    </div>
  );
};

/**
 * MetricGridWidget - Renders a grid of metric cards
 * 
 * Config structure:
 * {
 *   columns: 3,
 *   subHeader: { title: "위험성 평가 현황", popupType: "riskStatus" },
 *   items: [
 *     { label: "상", dataKey: "risk_stats.high", unit: "건", color: "red", showDot: true }
 *   ]
 * }
 */
const resolveItemValue = (item, data) => {
  if (item.source_key) {
    const sourceValue = getNestedValue(data, item.source_key, null);
    const prefix = `${item.source_key}.`;
    const keyName = typeof item.dataKey === 'string' && item.dataKey.startsWith(prefix)
      ? item.dataKey.slice(prefix.length)
      : item.dataKey;

    if (Array.isArray(sourceValue)) {
      if (keyName) {
        const matched = sourceValue.find((row) => String(row?.key) === String(keyName));
        if (matched && Object.prototype.hasOwnProperty.call(matched, 'value')) {
          return matched.value;
        }
      }
    } else if (sourceValue && typeof sourceValue === 'object') {
      if (keyName) {
        const nestedValue = getNestedValue(sourceValue, keyName, undefined);
        if (nestedValue !== undefined) {
          return nestedValue;
        }
      }
    }
  }

  // New Dynamic Binding Pattern
  if (item.target_table && item.target_column) {
    const key = `${item.target_table}.${item.target_column}`;
    return data[key];
  }

  // Legacy Pattern
  return getNestedValue(data, item.dataKey, 0);
};

const resolveUpdateCurrentValue = (item, data) => {
  const action = item.onClick || {};
  const sourceKey = action.source_key || item.source_key;
  const dataKey = action.dataKey;

  if (sourceKey && dataKey && sourceKey !== dataKey) {
    return getNestedValue(data, `${sourceKey}.${dataKey}`);
  }

  if (!sourceKey && dataKey) {
    return getNestedValue(data, dataKey);
  }

  return resolveItemValue(item, data);
};

export default function MetricGridWidget({ config, data, sendMessage, onAction }) {
  const normalizedConfig = Array.isArray(config) ? { items: config } : (config || {});
  const items = normalizedConfig.items || [];
  const widgetTitle = normalizedConfig.title || normalizedConfig.subHeader?.title || '';
  const [updateTarget, setUpdateTarget] = useState(null); // { idx, item, value }
  const [updateLoading, setUpdateLoading] = useState(false);
  const cardRefs = useRef({});
  
  if (!Array.isArray(items) || items.length === 0) return null;

  const columns = normalizedConfig.columns || 2;
  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3'
  }[columns] || 'grid-cols-2';

  const handleCardClick = (idx, item, currentValue) => {
    if (item.onClick?.type === 'update') {
      if (sendMessage) {
        sendMessage("Canvas", "SetCaptureAllKeyboardInput", "false");
      }
      setUpdateTarget({ idx, item, value: currentValue });
    }
  };

  const handleUpdateConfirm = async (newValue) => {
    if (!updateTarget || !onAction) return;
    setUpdateLoading(true);
    try {
      await onAction('updateValue', {
        source_key: updateTarget.item.onClick?.source_key || updateTarget.item.source_key,
        dataKey: updateTarget.item.onClick?.dataKey,
        value: newValue,
      });
    } finally {
      setUpdateLoading(false);
      if (sendMessage) {
        sendMessage("Canvas", "SetCaptureAllKeyboardInput", "true");
      }
      setUpdateTarget(null);
    }
  };

  return (
    <div>
      {widgetTitle && (
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-bold">{widgetTitle}</span>
        </div>
      )}
      <div className={`grid ${gridClass} gap-2 px-1`}>
        {items.map((item, idx) => {
          const rawValue = resolveItemValue(item, data);
          const displayVal = safeDisplayValue(rawValue, 0);
          const resolvedColor = resolveConditionalColor(displayVal, item.color || 'slate', item.colorRules);
          const hasUpdateAction = item.onClick?.type === 'update';

          // Create a ref for this card
          if (!cardRefs.current[idx]) {
            cardRefs.current[idx] = { current: null };
          }
          
          return (
            <div key={idx} className="relative" ref={(el) => { cardRefs.current[idx] = { current: el }; }}>
              <MetricCard
                label={item.label}
                value={displayVal}
                unit={item.unit || ''}
                color={resolvedColor}
                showDot={item.showDot}
                hasUpdateAction={hasUpdateAction}
                onClick={hasUpdateAction ? () => handleCardClick(idx, item, resolveUpdateCurrentValue(item, data)) : undefined}
              />
              {updateTarget?.idx === idx && (
                <UpdatePopover
                  isOpen={true}
                  anchorRef={cardRefs.current[idx]}
                  label={item.label}
                  currentValue={updateTarget.value}
                  unit={item.onClick?.unit || item.unit}
                  inputType={item.onClick?.inputType || 'number'}
                  loading={updateLoading}
                  onConfirm={handleUpdateConfirm}
                  onClose={() => {
                    if (sendMessage) {
                      sendMessage("Canvas", "SetCaptureAllKeyboardInput", "true");
                    }
                    setUpdateTarget(null);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
